import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    input,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslatePipe } from '@ngx-translate/core';
import { UploadedFile } from '../../models/uploaded-file';
import { ConstantsService } from '../../services/constants.service';
import { formatFileSize } from '../../utils/formatFileSize';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';

/**
 * Represents the columns of a table for file uploads
 */
type FileUploadColumns = {
    fileName: string;
    size: string;
    status: string;
    action: string;
};

@Component({
    selector: 'apcam-files-table',
    templateUrl: './files-table.component.html',
    styleUrl: './files-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AsyncPipe,
        LoadingSpinnerComponent,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatTableModule,
        TooltipCustomComponent,
        TranslatePipe,
    ],
})
export class FilesTableComponent {
    readonly files = input.required<UploadedFile[]>();

    /**
     * WeakMap keyed by {@link UploadedFile} instance. Template: filesDeleting().get(file).
     * Mutations are in-place; parent bumps deleteUiRevision to trigger OnPush CD.
     */
    readonly filesDeleting = input.required<WeakMap<UploadedFile, boolean>>();

    /**
     * CD-only revision from parent ({@link FileUploadComponent#deleteUiRevision}).
     * Template must read deleteUiRevision() in the delete-spinner branch.
     */
    readonly deleteUiRevision = input(0);

    readonly onRemoveFile = output<number>();

    DEFAULT_DECIMAL_POINTS = ConstantsService.DEFAULT_DECIMAL_POINTS;

    columns: FileUploadColumns = {
        fileName: 'fileName',
        size: 'size',
        status: 'status',
        action: 'action',
    };

    displayedColumns: string[] = Object.keys(this.columns);
    dataSource: MatTableDataSource<UploadedFile> =
        new MatTableDataSource<UploadedFile>();

    constructor() {
        effect(() => {
            this.dataSource.data = this.files();
        });
    }

    protected formatUploadedFileSize(
        bytes: number,
        decimalPoint: number
    ): string {
        return formatFileSize(bytes, decimalPoint);
    }

    protected removeFile(index: number): void {
        this.onRemoveFile.emit(index);
    }
}
